/// <reference types="w3c-web-usb" />

import {
  exportConfigAsText,
  initializeConfig,
  prepareConfigCommands
} from './config/ConfigStore';
import { fieldConfigs } from './config/fields';
import { handleFieldChange } from './handlers';
import { t } from './intl';
import type { AppState } from './render';
import { render } from './render';
import './style.css';
import { getDataEndpoint, getInEndpoint, sendConfigToDevice, sendSerialData, executeCommand } from './usb/serial';
import { closeDevice, getPairedDevices, isWebUSBSupported, openDevice } from './usb/usb';

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

const DEFAULT_BAUD_RATE = 115_200;

function createApp(): AppState {
  return {
    state: 'disconnected',
    config: initializeConfig(),
    elements: {} as AppState['elements'],
    error: null,
    deviceInfo: null,
    pairedDevices: [],
    connectionSettings: { baudRate: DEFAULT_BAUD_RATE },
    usbDevice: null,
    onDeviceDisconnect: null,
  };
}

function init(): void {
  const app = createApp();

  app.elements.app = document.getElementById('app')!;

  // Load paired devices on init
  if (isWebUSBSupported()) {
    getPairedDevices().then(devices => {
      app.pairedDevices = devices;
      if (app.state === 'disconnected') {
        render(app);
      }
    });
  }

  async function connectToDevice(device: USBDevice) {
    app.state = 'connecting';
    render(app);

    try {
      const info = await openDevice(device, app.connectionSettings.baudRate, () => {
        app.state = 'disconnected';
        app.usbDevice = null;
        app.deviceInfo = null;
        app.onDeviceDisconnect = null;
        render(app);
      });

      app.state = 'connected';
      app.usbDevice = device;
      app.deviceInfo = info;
      render(app);
    } catch (err) {
      const error = err as { message: string };
      app.state = 'error';
      app.error = error.message;
      render(app);
    }
  }

  async function connectNewDevice() {
    if (!isWebUSBSupported()) {
      app.state = 'error';
      app.error = t('errors.webusbNotSupported');
      render(app);
      return;
    }

    app.state = 'connecting';
    render(app);

    try {
      const device = await navigator.usb.requestDevice({ filters: [] });
      await connectToDevice(device);
    } catch (err) {
      const error = err as Error | DOMException;
      if (error.name === 'NotFoundError') {
        app.state = 'error';
        app.error = t('errors.noDeviceSelected');
      } else if (error.name === 'SecurityError') {
        app.state = 'error';
        app.error = t('errors.permissionDenied');
      } else {
        app.state = 'error';
        app.error = t('errors.failedToOpen', { error: error.message });
      }
      render(app);
    }
  }

  app.elements.app.addEventListener('click', async (e: Event) => {
    const target = e.target as HTMLElement;
    const deviceItem = target.closest('[data-device-index]');

    if (deviceItem) {
      const index = parseInt((deviceItem as HTMLElement).dataset.deviceIndex!, 10);
      const device = app.pairedDevices[index]?.device;
      if (device) {
        await connectToDevice(device);
      }
    } else if (target.id === 'connect-btn' || target.closest('#connect-btn')) {
      await connectNewDevice();
    } else if (target.id === 'retry-btn') {
      await connectNewDevice();
    } else if (target.id === 'back-btn') {
      app.state = 'disconnected';
      app.error = null;
      render(app);
    } else if (target.id === 'disconnect-btn') {
      if (app.usbDevice && app.onDeviceDisconnect) {
        await closeDevice(app.usbDevice);
      }
      app.usbDevice = null;
      app.onDeviceDisconnect = null;
      app.state = 'disconnected';
      app.deviceInfo = null;
      getPairedDevices().then(devices => {
        app.pairedDevices = devices;
        render(app);
      });
    } else if (target.id === 'serial-send-btn') {
      const input = document.getElementById('serial-input') as HTMLInputElement;
      if (input && input.value && app.usbDevice) {
        const endpointOut = getDataEndpoint(app.usbDevice);
        const endpointIn = getInEndpoint(app.usbDevice);
        if (endpointOut !== null && endpointIn !== null) {
          const response = await executeCommand(app.usbDevice, endpointOut, endpointIn, input.value);
          console.log('[CLI] Response:', response);
        }
        input.value = '';
      }
    } else if (target.id === 'save-btn') {
      if (!app.usbDevice) {
        alert(t('alerts.noDevice'));
        return;
      }

      const saveBtn = target as HTMLButtonElement;
      saveBtn.disabled = true;
      saveBtn.textContent = t('alerts.saving');

      try {
        const success = await sendConfigToDevice(app.usbDevice, prepareConfigCommands(app.config));
        if (success) {
          alert(t('alerts.saveSuccess'));
        } else {
          alert(t('alerts.saveFailure'));
        }
      } catch (err) {
        const error = err as { message: string };
        alert(t('alerts.saveError', { error: error.message }));
      } finally {
        saveBtn.disabled = false;
        saveBtn.textContent = t('connected.saveToDevice');
      }
    } else if (target.id === 'export-btn') {
      const textContent = exportConfigAsText(app.config);
      const blob = new Blob([textContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'device-config.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  });

  app.elements.app.addEventListener('input', (e: Event) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    if (target.id === 'connection-baud-rate') {
      app.connectionSettings.baudRate = Number(target.value);
      return;
    }
    if (target.id) {
      const newApp = handleFieldChange(app, target.id, target.value);
      app.config = newApp.config;
    }
  });

  app.elements.app.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLInputElement;
    if (target.type === 'checkbox' && target.id) {
      const newApp = handleFieldChange(app, target.id, String(target.checked));
      app.config = newApp.config;
    }
  });

  app.elements.app.addEventListener('keydown', async (e: KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (target.id === 'serial-input' && e.key === 'Enter') {
      const sendBtn = document.getElementById('serial-send-btn');
      if (sendBtn) sendBtn.click();
    }
  });

  app.elements.app.addEventListener('focusin', (e: Event) => {
    const target = e.target as HTMLElement;
    if (target.id && target.closest('.form-group') && target.id !== 'connection-baud-rate') {
      const fieldConfig = fieldConfigs.find((f) => f.register === target.id);
      const helpText = document.getElementById('help-text');
      if (helpText && fieldConfig) {
        helpText.textContent = t(fieldConfig.help);
      }
    }
  });

  app.elements.app.addEventListener('focusout', (e: Event) => {
    const target = e.target as HTMLElement;
    if (target.id && target.closest('.form-group') && target.id !== 'connection-baud-rate') {
      const helpText = document.getElementById('help-text');
      if (helpText) {
        helpText.textContent = t('help.default');
      }
    }
  });

  render(app);
}

init();
